import { useContext, useRef, useState } from "react";
import { toast } from "react-toastify";
import { GlobalContext } from "../../Context/GlobalContext";

const isSuccess = (data) =>
  !data?.responseCode || String(data.responseCode) === "00";

const extractRows = (data) => {
  const value = data?.response ?? data?.data ?? data?.result ?? [];
  if (Array.isArray(value)) return value;
  return value && typeof value === "object" ? [value] : [];
};

const getErrorMessage = (error, fallback) =>
  error?.response?.data?.responseMessage ||
  error?.response?.data?.message ||
  error?.message ||
  fallback;

export default function useBulkAddons() {
  const { api, userID } = useContext(GlobalContext);

  const bulkCatalogIdRef = useRef("");
  const bulkCatalogPromiseRef = useRef(null);

  const resolveBulkCatalogId = async ({ force = false } = {}) => {
    const cached = String(bulkCatalogIdRef.current || "").trim();
    if (!force && cached) return cached;
    if (!force && bulkCatalogPromiseRef.current)
      return bulkCatalogPromiseRef.current;

    const request = (async () => {
      try {
        const usCode = String(userID || "").trim();
        if (!usCode) return "";

        const res = await api.post("/Inventory/getCatalogIdUSCode", { usCode });
        if (String(res?.data?.responseCode ?? "") !== "00") {
          toast.error(
            res?.data?.responseMessage ||
              "Failed to fetch catalogue mapping ID",
          );
          return "";
        }

        const response = res?.data?.response;
        const mappingId = Array.isArray(response)
          ? response?.[0]?.mapping_id
          : response?.mapping_id;
        const catalogId = String(mappingId ?? "").trim();
        if (!catalogId) {
          toast.error("Catalogue mapping ID not found");
          return "";
        }

        bulkCatalogIdRef.current = catalogId;
        return catalogId;
      } catch (error) {
        console.error(
          "getCatalogIdUSCode error:",
          error?.response?.data || error,
        );
        toast.error(
          error?.response?.data?.responseMessage ||
            error?.response?.data?.message ||
            error?.message ||
            "Error fetching catalogue mapping ID",
        );
        return "";
      } finally {
        bulkCatalogPromiseRef.current = null;
      }
    })();

    bulkCatalogPromiseRef.current = request;
    return request;
  };

  const [addonsLoading, setAddonsLoading] = useState(false);
  const [addonSaving, setAddonSaving] = useState(false);
  const [addonDeletingId, setAddonDeletingId] = useState("");

  const [packageAddonsLoading, setPackageAddonsLoading] = useState(false);
  const [packageAddonSaving, setPackageAddonSaving] = useState(false);
  const [packageAddonDeletingKey, setPackageAddonDeletingKey] = useState("");

  const getAddons = async (addonId = "") => {
    try {
      setAddonsLoading(true);
      const res = await api.post("/BulkCatalogue/addonsGet", {
        catalog_id: await resolveBulkCatalogId(),
        addon_id: String(addonId ?? "").trim(),
      });

      if (!isSuccess(res?.data)) {
        toast.error(res?.data?.responseMessage || "Failed to fetch addons");
        return [];
      }

      return extractRows(res?.data);
    } catch (error) {
      console.error("addonsGet error:", error?.response?.data || error);
      toast.error(getErrorMessage(error, "Error fetching addons"));
      return [];
    } finally {
      setAddonsLoading(false);
    }
  };

  const buildAddonPayload = (form = {}, includeId = false) => ({
    ...(includeId ? { addon_id: String(form?.addon_id ?? "").trim() } : {}),
    restaurant_id: String(form?.restaurant_id ?? "").trim(),
    addon_name: String(form?.addon_name ?? "").trim(),
    linked_item_id: String(form?.linked_item_id ?? "").trim(),
    linked_variant_id: String(form?.linked_variant_id ?? "").trim(),
    price_type: String(form?.price_type ?? "").trim(),
    price: String(form?.price ?? "").trim(),
    status: String(form?.status ?? "ACTIVE").trim(),
  });

  const saveAddon = async (form, editing) => {
    if (addonSaving) return false;

    const payload = buildAddonPayload(form, editing);

    if (editing && !payload.addon_id) {
      toast.error("Addon ID is required");
      return false;
    }
    if (!payload.restaurant_id) {
      toast.error("Restaurant is required");
      return false;
    }
    if (!payload.addon_name) {
      toast.error("Addon name is required");
      return false;
    }

    try {
      setAddonSaving(true);
      const res = await api.post(
        editing ? "/BulkCatalogue/addonsUpdate" : "/BulkCatalogue/addonsInsert",
        payload,
      );

      if (!isSuccess(res?.data)) {
        toast.error(res?.data?.responseMessage || "Addon save failed");
        return false;
      }

      toast.success(
        res?.data?.responseMessage ||
          `Addon ${editing ? "updated" : "added"} successfully`,
      );
      return true;
    } catch (error) {
      console.error("Addon save error:", error?.response?.data || error);
      toast.error(getErrorMessage(error, "Error saving addon"));
      return false;
    } finally {
      setAddonSaving(false);
    }
  };

  const insertAddon = (form) => saveAddon(form, false);
  const updateAddon = (form) => saveAddon(form, true);

  const deleteAddon = async (addonId) => {
    const id = String(addonId ?? "").trim();
    if (!id || addonDeletingId) return false;

    try {
      setAddonDeletingId(id);
      const res = await api.post("/BulkCatalogue/addonsDelete", {
        addon_id: id,
      });

      if (!isSuccess(res?.data)) {
        toast.error(res?.data?.responseMessage || "Addon delete failed");
        return false;
      }

      toast.success(res?.data?.responseMessage || "Addon deleted successfully");
      return true;
    } catch (error) {
      console.error("addonsDelete error:", error?.response?.data || error);
      toast.error(getErrorMessage(error, "Error deleting addon"));
      return false;
    } finally {
      setAddonDeletingId("");
    }
  };

  const getPackageAddons = async (packageId = "", addonId = "") => {
    try {
      setPackageAddonsLoading(true);
      const res = await api.post("/BulkCatalogue/packageAddonsGet", {
        catalog_id: await resolveBulkCatalogId(),
        package_id: String(packageId ?? "").trim(),
        addon_id: String(addonId ?? "").trim(),
      });

      if (!isSuccess(res?.data)) {
        toast.error(
          res?.data?.responseMessage || "Failed to fetch package addons",
        );
        return [];
      }

      return extractRows(res?.data);
    } catch (error) {
      console.error("packageAddonsGet error:", error?.response?.data || error);
      toast.error(getErrorMessage(error, "Error fetching package addons"));
      return [];
    } finally {
      setPackageAddonsLoading(false);
    }
  };

  const insertPackageAddon = async (form = {}) => {
    if (packageAddonSaving) return false;

    const payload = {
      package_id: String(form?.package_id ?? "").trim(),
      addon_id: String(form?.addon_id ?? "").trim(),
    };

    if (!payload.package_id || !payload.addon_id) {
      toast.error("Package and Addon are required");
      return false;
    }

    try {
      setPackageAddonSaving(true);
      const res = await api.post("/BulkCatalogue/packageAddonsInsert", payload);

      if (!isSuccess(res?.data)) {
        toast.error(res?.data?.responseMessage || "Package addon save failed");
        return false;
      }

      toast.success(
        res?.data?.responseMessage || "Package addon added successfully",
      );
      return true;
    } catch (error) {
      console.error(
        "packageAddonsInsert error:",
        error?.response?.data || error,
      );
      toast.error(getErrorMessage(error, "Error adding package addon"));
      return false;
    } finally {
      setPackageAddonSaving(false);
    }
  };

  const updatePackageAddon = async (form = {}) => {
    if (packageAddonSaving) return false;

    const payload = {
      package_id: String(form?.package_id ?? "").trim(),
      addon_id: String(form?.addon_id ?? "").trim(),
      new_package_id: String(form?.new_package_id ?? "").trim(),
      new_addon_id: String(form?.new_addon_id ?? "").trim(),
    };

    if (
      !payload.package_id ||
      !payload.addon_id ||
      !payload.new_package_id ||
      !payload.new_addon_id
    ) {
      toast.error("All package addon mapping fields are required");
      return false;
    }

    try {
      setPackageAddonSaving(true);
      const res = await api.post("/BulkCatalogue/packageAddonsUpdate", payload);

      if (!isSuccess(res?.data)) {
        toast.error(
          res?.data?.responseMessage || "Package addon update failed",
        );
        return false;
      }

      toast.success(
        res?.data?.responseMessage || "Package addon updated successfully",
      );
      return true;
    } catch (error) {
      console.error(
        "packageAddonsUpdate error:",
        error?.response?.data || error,
      );
      toast.error(getErrorMessage(error, "Error updating package addon"));
      return false;
    } finally {
      setPackageAddonSaving(false);
    }
  };

  const deletePackageAddon = async (packageId, addonId) => {
    const packageValue = String(packageId ?? "").trim();
    const addonValue = String(addonId ?? "").trim();
    const key = `${packageValue}:${addonValue}`;

    if (!packageValue || !addonValue || packageAddonDeletingKey) return false;

    try {
      setPackageAddonDeletingKey(key);
      const res = await api.post("/BulkCatalogue/packageAddonsDelete", {
        package_id: packageValue,
        addon_id: addonValue,
      });

      if (!isSuccess(res?.data)) {
        toast.error(
          res?.data?.responseMessage || "Package addon delete failed",
        );
        return false;
      }

      toast.success(
        res?.data?.responseMessage || "Package addon deleted successfully",
      );
      return true;
    } catch (error) {
      console.error(
        "packageAddonsDelete error:",
        error?.response?.data || error,
      );
      toast.error(getErrorMessage(error, "Error deleting package addon"));
      return false;
    } finally {
      setPackageAddonDeletingKey("");
    }
  };

  return {
    addonsLoading,
    addonSaving,
    addonDeletingId,
    getAddons,
    insertAddon,
    updateAddon,
    deleteAddon,

    packageAddonsLoading,
    packageAddonSaving,
    packageAddonDeletingKey,
    getPackageAddons,
    insertPackageAddon,
    updatePackageAddon,
    deletePackageAddon,
  };
}
